import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Compass, Home, AlertTriangle, Lock } from 'lucide-react';

/**
 * Écran d'erreur terminal. `code` bascule entre « introuvable » (404) et
 * « accès refusé » (403).
 *
 * Le 403 reste DÉLIBÉRÉMENT muet sur la ressource (ni nom, ni type, ni détail) :
 * distinguer les deux cas apprend déjà à qui balaie les id qu'une entité existe
 * à cette adresse — inutile d'y ajouter de quoi la caractériser.
 */
export default function NotFoundPage({ title, message, technical, code = 404 }) {
    const location = useLocation();
    const navigate = useNavigate();
    const isDenied = code === 403;

    return (
        <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 flex items-center justify-center p-6">
            <div className="w-full max-w-md text-center">

                <div className="relative mx-auto mb-10 w-48 h-48 select-none">
                    <div className="absolute inset-0 rounded-full bg-stone-200/40 animate-pulse" />
                    <div className="absolute inset-4 rounded-full bg-stone-200/60" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-8xl font-extrabold tracking-tighter bg-gradient-to-b from-stone-400 to-stone-300 bg-clip-text text-transparent">
                            {code}
                        </span>
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-12 h-12 rounded-full bg-white shadow-lg border border-stone-200 flex items-center justify-center">
                        {isDenied
                            ? <Lock size={22} className="text-stone-400" />
                            : <Compass size={22} className="text-stone-400" style={{ animation: 'notFoundSpin 4s ease-in-out infinite' }} />}
                    </div>
                </div>

                <h1 className="text-xl font-bold text-stone-800 mb-2">
                    {title || (isDenied ? 'Accès refusé' : 'Page introuvable')}
                </h1>

                <p className="text-sm text-stone-500 leading-relaxed mb-8 max-w-xs mx-auto">
                    {message || (isDenied
                        ? 'Vous n\'avez pas les droits nécessaires pour consulter cette page.'
                        : 'Cette page n\'existe pas ou a été déplacée. Vérifiez l\'adresse ou revenez à l\'accueil.')}
                </p>

                {technical && (
                    <div className="flex items-start gap-2.5 text-left text-xs text-amber-700 bg-amber-50/80 border border-amber-200/60 rounded-xl px-4 py-3 mb-6 backdrop-blur-sm">
                        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                        <span>{technical}</span>
                    </div>
                )}

                <div className="flex items-center justify-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-stone-600 bg-white border border-stone-200 rounded-xl shadow-sm hover:bg-stone-50 hover:shadow-md transition-all"
                    >
                        <ArrowLeft size={15} />
                        Retour
                    </button>
                    <a
                        href="/"
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-stone-800 rounded-xl shadow-sm hover:bg-stone-700 hover:shadow-md transition-all"
                    >
                        <Home size={15} />
                        Accueil
                    </a>
                </div>

                <p className="text-xs text-stone-400 mt-10 font-mono tracking-wide">
                    {location.pathname}
                </p>

                <style>{`
                    @keyframes notFoundSpin {
                        0%, 100% { transform: rotate(0deg); }
                        25% { transform: rotate(90deg); }
                        50% { transform: rotate(45deg); }
                        75% { transform: rotate(-30deg); }
                    }
                `}</style>
            </div>
        </div>
    );
}
